import { Badge, Column, Table } from '@odin-labs/components';
import { LoadingError } from 'components/loadingError';
import { Section } from 'components/section';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSiteLayout } from 'siteLayout';
import {
  CURRENT_VERSION,
  LayoutVersionRow,
  siteLayoutDesignerPath,
  toLayoutVersionRows,
  versionStatusBadge,
} from './layoutVersions';

function VersionCell({
  row,
}: {
  row: { original: LayoutVersionRow };
}): React.ReactElement {
  const badge = versionStatusBadge[row.original.status];
  return (
    <span className="odin-flex odin-items-center odin-gap-2">
      <span className="odin-font-medium odin-text-gray-900">
        {row.original.name}
      </span>
      {badge && <Badge text={badge.text} color={badge.color} />}
    </span>
  );
}

const columns: Column<LayoutVersionRow>[] = [
  { id: 'name', Header: 'Version', accessor: 'name', Cell: VersionCell },
  { id: 'publishedAt', Header: 'Published', accessor: 'publishedAt' },
  { id: 'publishedBy', Header: 'Published by', accessor: 'publishedBy' },
];

/**
 * The site's layout versions below the geofencing form: the draft when there
 * is one, then every published revision, newest first, with when and by whom.
 * A row opens that version in the designer.
 */
export function LayoutVersionsPanel({
  jobsiteId,
  onApiChange,
}: {
  jobsiteId: string;
  /** Reports the panel's reload so the tab's header refreshes it with the form. */
  onApiChange?: (api: { refetch: () => void; loading: boolean }) => void;
}): React.ReactElement {
  const history = useHistory();
  const { state, loading, error, refetch } = useSiteLayout(jobsiteId);

  React.useEffect(
    () => onApiChange?.({ refetch, loading }),
    [onApiChange, refetch, loading],
  );

  const rows = React.useMemo(
    () => (state ? toLayoutVersionRows(state) : []),
    [state],
  );

  return (
    <Section
      id="layoutVersions"
      title="Layout versions"
      details="Every published version of the site's layout, and the draft when one is open. Open one to see it in the designer."
      className="odin-border-t odin-border-gray-200"
    >
      <div className="odin-col-span-6">
        {error && <LoadingError error={error.message} />}
        {!error && (
          <Table
            columns={columns}
            data={rows}
            loading={!state && loading}
            noResultsText="No layout yet. Open the designer to add the first zone."
            onRowClick={({ data: row }) =>
              history.push(siteLayoutDesignerPath(jobsiteId, row.id))
            }
            remote
            disableGlobalFilter
            disableSortBy
          />
        )}
        {/* A site with no layout has no row to open the designer from. */}
        {!error && state && !rows.length && (
          <Link
            to={siteLayoutDesignerPath(jobsiteId, CURRENT_VERSION)}
            className="odin-mt-3 odin-inline-block odin-text-sm odin-font-medium odin-text-odin-primary hover:odin-underline"
          >
            Open the designer
          </Link>
        )}
      </div>
    </Section>
  );
}
